import React, { useEffect, useMemo, useState } from "react";

import { Activity, BarChart3, MousePointerClick, Users } from "lucide-react";

import MetricCards from "../../Components/MetricCards";
import TabsComponent from "../../Components/Tab";
import Table from "../../Components/Table";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import {
  clearOrgProductTelemetryState,
  fetchOrgProductTelemetry,
} from "../../Store/Reducers/AdminSlice";
import {
  getAccountTeamRoleDisplayLabel,
  isAccountTeamRole,
  normalizeRoleName,
  ROLE_DISPLAY_NAMES,
  RoleType,
} from "../../Utils/Constants/RoleConstants";
import { toast } from "../../Utils/Helper/toast";
import "./style.scss";

export interface OrgProductTelemetryPanelProps {
  /** Tenant UUID. Required for izba_admin; optional for vendor_admin (scoped by header). */
  orgId?: string | null;
  organizationName?: string;
  /** Extra class on outer wrapper */
  className?: string;
  /** Show org name / date line above cards (flat page). Modal supplies its own subtitle. */
  showHeader?: boolean;
  /** Called when API says product telemetrics is not entitled for this org. */
  onAccessDenied?: () => void;
}

type MetricFamily =
  | "daily_active_users"
  | "weekly_active_users"
  | "screen_page_visits"
  | "element_interactions";

type DetailTab = "role" | "users";

const FAMILY_ORDER: MetricFamily[] = [
  "daily_active_users",
  "weekly_active_users",
  "screen_page_visits",
  "element_interactions",
];

const FAMILY_META: Record<
  MetricFamily,
  {
    title: string;
    borderType: "blue" | "green" | "orange" | "purple";
    icon: React.ReactNode;
    hasUsers: boolean;
    /** Short window label for the pill, e.g. "Yesterday" */
    windowLabel: string;
    /** What the card counts, e.g. "Unique people" / "Clicks" */
    measureLabel: string;
    /** Plain-language definition under the detail title */
    cardHint: string;
    /** Extra context clarifying vs other KPIs / tabs */
    explanation: string;
    roleCountTitle: string;
    roleHint: string;
    userCountTitle: string;
    userHint: string;
  }
> = {
  daily_active_users: {
    title: "Daily Active Users",
    borderType: "blue",
    icon: <Users size={20} />,
    hasUsers: true,
    windowLabel: "Yesterday",
    measureLabel: "Unique people",
    cardHint: "People who opened at least one page yesterday",
    explanation:
      "Counts unique people for this date only — not the last 7 days. Someone in Weekly Active Users may be missing here if they had no pageviews on this day.",
    roleCountTitle: "Users",
    roleHint: "Unique people per role on this date",
    userCountTitle: "Pageviews",
    userHint:
      "Page opens each person made on this date — not the unique-people total on the card",
  },
  weekly_active_users: {
    title: "Weekly Active Users",
    borderType: "green",
    icon: <Activity size={20} />,
    hasUsers: true,
    windowLabel: "Last 7 days",
    measureLabel: "Unique people",
    cardHint: "People who opened at least one page in the last 7 days",
    explanation:
      "Trailing 7 days ending on this date. This list is often larger than Daily Active Users or yesterday-only click metrics.",
    roleCountTitle: "Users",
    roleHint: "Unique people per role over the last 7 days",
    userCountTitle: "Pageviews",
    userHint:
      "Page opens each person made in the last 7 days — not the unique-people total on the card",
  },
  screen_page_visits: {
    title: "Screen Page Visits",
    borderType: "purple",
    icon: <BarChart3 size={20} />,
    hasUsers: true,
    windowLabel: "Yesterday",
    measureLabel: "Page views",
    cardHint: "Page opens on product screens yesterday (not unique people)",
    explanation:
      "Event volume for this date only. One person can add many page views. Unmapped screens are excluded.",
    roleCountTitle: "Pageviews",
    roleHint: "Page opens per role and screen on this date",
    userCountTitle: "Pageviews",
    userHint: "Page opens each person made on that screen on this date",
  },
  element_interactions: {
    title: "Element Interactions",
    borderType: "orange",
    icon: <MousePointerClick size={20} />,
    hasUsers: true,
    windowLabel: "Yesterday",
    measureLabel: "Clicks",
    cardHint: "Clicks and UI interactions yesterday",
    explanation:
      "Clicks on this date only — not the last 7 days. People listed under Weekly Active Users appear here only if they also clicked on this date.",
    roleCountTitle: "Interactions",
    roleHint: "Clicks per role on this date",
    userCountTitle: "Interactions",
    userHint: "Clicks each person made on this date",
  },
};

const matchesFamily = (metricKey: string, family: MetricFamily): boolean => {
  if (family === "screen_page_visits") {
    return (
      metricKey === "screen_page_visits" ||
      metricKey.startsWith("screen_page_visits:")
    );
  }
  return metricKey === family;
};

const formatRole = (role: string) => {
  const normalized = normalizeRoleName(role);
  if (!normalized) return role || "—";

  // PostHog may stamp executive_sponsor_leader as an ESL alias
  const canonical =
    normalized === "executive_sponsor_leader"
      ? "executive_sponsor_lead"
      : normalized;

  if (canonical in ROLE_DISPLAY_NAMES) {
    return ROLE_DISPLAY_NAMES[canonical as RoleType];
  }
  if (isAccountTeamRole(canonical)) {
    return getAccountTeamRoleDisplayLabel(canonical);
  }
  return canonical
    .split("_")
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

/** Display metric_date (YYYY-MM-DD) as MM/DD/YYYY without timezone shift. */
const formatMetricDate = (isoDate: string | null | undefined): string => {
  if (!isoDate) return "";
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(isoDate).trim());
  if (match) {
    const [, year, month, day] = match;
    return `${month}/${day}/${year}`;
  }
  return String(isoDate);
};

const isProductTelemetricsAccessDenied = (message: string): boolean =>
  /product telemetrics is not enabled/i.test(message);

const OrgProductTelemetryPanel: React.FC<OrgProductTelemetryPanelProps> = ({
  orgId,
  organizationName,
  className,
  showHeader = false,
  onAccessDenied,
}) => {
  const dispatch = useAppDispatch();
  const {
    metrics,
    users,
    loading,
    error: loadError,
  } = useAppSelector(state => state.admin.orgProductTelemetry);
  const [activeFamily, setActiveFamily] =
    useState<MetricFamily>("daily_active_users");
  const [detailTab, setDetailTab] = useState<DetailTab>("role");

  useEffect(() => {
    let cancelled = false;
    setActiveFamily("daily_active_users");
    setDetailTab("role");

    void (async () => {
      try {
        await dispatch(fetchOrgProductTelemetry({ orgId })).unwrap();
      } catch (error: unknown) {
        if (cancelled) return;
        const message =
          typeof error === "string" && error.trim()
            ? error
            : "Failed to load telemetric data";
        toast.error(message);
        if (isProductTelemetricsAccessDenied(message)) {
          onAccessDenied?.();
        }
      }
    })();

    return () => {
      cancelled = true;
      dispatch(clearOrgProductTelemetryState());
    };
  }, [dispatch, orgId, onAccessDenied]);

  const latestMetricDate = useMemo(() => {
    if (!metrics.length) return null;
    return metrics.reduce(
      (latest, row) => (row.metric_date > latest ? row.metric_date : latest),
      metrics[0].metric_date
    );
  }, [metrics]);

  const datedMetrics = useMemo(() => {
    if (!latestMetricDate) return [];
    return metrics.filter(row => row.metric_date === latestMetricDate);
  }, [metrics, latestMetricDate]);

  const datedUsers = useMemo(() => {
    if (!latestMetricDate) return [];
    return users.filter(row => row.metric_date === latestMetricDate);
  }, [users, latestMetricDate]);

  const familySummaries = useMemo(() => {
    return FAMILY_ORDER.map(family => {
      const rows = datedMetrics.filter(row =>
        matchesFamily(row.metric_key, family)
      );
      const total = rows.reduce((sum, row) => sum + (row.metric_value || 0), 0);

      const byRoleMap = new Map<string, number>();
      rows.forEach(row => {
        const key =
          family === "screen_page_visits"
            ? `${row.role}::${row.screen_key || row.metric_key.split(":")[1] || "—"}`
            : row.role;
        byRoleMap.set(key, (byRoleMap.get(key) || 0) + (row.metric_value || 0));
      });

      const roleRows =
        family === "screen_page_visits"
          ? Array.from(byRoleMap.entries()).map(([key, value]) => {
              const [role, screen] = key.split("::");
              return { id: key, role, screen, value };
            })
          : Array.from(byRoleMap.entries()).map(([role, value]) => ({
              id: role,
              role,
              screen: null as string | null,
              value,
            }));

      const userRows = datedUsers
        .filter(row => matchesFamily(row.metric_key, family))
        .map(row => ({
          id: row.id,
          name: row.name || "—",
          email: row.email || "—",
          role: row.role,
          screen: row.screen_key || "",
          activity_count: row.activity_count,
        }))
        .sort((a, b) => b.activity_count - a.activity_count);

      return { family, total, roleRows, userRows };
    });
  }, [datedMetrics, datedUsers]);

  const activeSummary = useMemo(
    () =>
      familySummaries.find(item => item.family === activeFamily) ||
      familySummaries[0],
    [familySummaries, activeFamily]
  );

  const activeMeta = FAMILY_META[activeFamily];

  const selectFamily = (family: MetricFamily) => {
    setActiveFamily(family);
    setDetailTab("role");
  };

  const roleColumns = useMemo(() => {
    const cols: any[] = [
      {
        key: "role",
        title: "ROLE",
        render: (value: string) => formatRole(value),
      },
    ];
    if (activeFamily === "screen_page_visits") {
      cols.push({
        key: "screen",
        title: "SCREEN",
        render: (value: string | null) => value || "—",
      });
    }
    cols.push({
      key: "value",
      title: activeMeta.roleCountTitle.toUpperCase(),
      textalign: "right" as const,
      render: (value: number) => value.toLocaleString(),
    });
    return cols;
  }, [activeFamily, activeMeta.roleCountTitle]);

  const userColumns = useMemo(() => {
    const cols: any[] = [
      { key: "name", title: "NAME" },
      { key: "email", title: "EMAIL" },
      {
        key: "role",
        title: "ROLE",
        render: (value: string) => formatRole(value),
      },
    ];
    if (activeFamily === "screen_page_visits") {
      cols.push({
        key: "screen",
        title: "SCREEN",
        render: (value: string) => value || "—",
      });
    }
    cols.push({
      key: "activity_count",
      title: activeMeta.userCountTitle.toUpperCase(),
      textalign: "right" as const,
      render: (value: number) => value.toLocaleString(),
    });
    return cols;
  }, [activeFamily, activeMeta.userCountTitle]);

  const detailTabs = [
    { id: "role", label: "By role" },
    { id: "users", label: "Users", isHide: !activeMeta.hasUsers },
  ];

  const showingUsers = detailTab === "users" && activeMeta.hasUsers;
  const tableData = showingUsers
    ? activeSummary?.userRows || []
    : activeSummary?.roleRows || [];
  const tableColumns = showingUsers ? userColumns : roleColumns;

  return (
    <div className={`org-product-telemetry-panel ${className || ""}`}>
      {showHeader ? (
        <div className="mb-5">
          <h2 className="text-xl font-semibold text-gray-900 mb-1">
            Telemetry Data
          </h2>
          <p className="text-sm text-gray-500 mb-0">
            {organizationName
              ? `Product usage for ${organizationName}`
              : "Product usage for your organization"}
            {latestMetricDate ? ` · ${formatMetricDate(latestMetricDate)}` : ""}
          </p>
        </div>
      ) : null}

      {!loadError ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-5">
          {familySummaries.map(({ family, total }) => (
            <div
              key={family}
              className={`org-product-telemetry-panel__card ${
                activeFamily === family ? "is-active" : ""
              }`}
              title={FAMILY_META[family].cardHint}
            >
              <MetricCards
                metrics={[
                  {
                    title: FAMILY_META[family].title,
                    value: loading ? "…" : total,
                    subtitle: latestMetricDate
                      ? `As of ${formatMetricDate(latestMetricDate)}`
                      : "No data yet",
                    borderType: FAMILY_META[family].borderType,
                    icon: FAMILY_META[family].icon,
                    onClick: () => selectFamily(family),
                  },
                ]}
              />
            </div>
          ))}
        </div>
      ) : null}

      {loading ? (
        <div className="text-sm text-gray-500 py-8 text-center">
          Loading telemetric data…
        </div>
      ) : null}

      {!loading && loadError ? (
        <div className="text-sm text-red-600 py-8 text-center">{loadError}</div>
      ) : null}

      {!loading && !loadError && !datedMetrics.length ? (
        <div className="text-sm text-gray-500 py-8 text-center">
          No telemetric data found for this organization yet.
        </div>
      ) : null}

      {!loading && !loadError && datedMetrics.length > 0 && activeSummary ? (
        <div className="org-product-telemetry-panel__detail">
          <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <h3 className="text-base font-semibold text-gray-900 mb-0">
                  {activeMeta.title}
                </h3>
                <span className="org-product-telemetry-panel__pill">
                  {activeMeta.windowLabel}
                  <span className="org-product-telemetry-panel__pill-sep">
                    ·
                  </span>
                  {activeMeta.measureLabel}
                </span>
              </div>
              <p className="text-sm text-gray-600 mb-0 mt-0.5">
                Total {activeSummary.total.toLocaleString()}
                {latestMetricDate
                  ? ` · ${formatMetricDate(latestMetricDate)}`
                  : ""}
              </p>
              <p className="text-sm text-gray-700 mb-0 mt-1 font-medium">
                {activeMeta.cardHint}
              </p>
            </div>
            <TabsComponent
              tabs={detailTabs}
              activeTab={activeMeta.hasUsers ? detailTab : "role"}
              onTabChange={(id: DetailTab) => setDetailTab(id)}
              variant="secondary-segmented"
              segmentedLayout="fit"
              className="rounded-full"
            />
          </div>

          <div className="org-product-telemetry-panel__callout" role="note">
            <p className="org-product-telemetry-panel__callout-title">
              What this measures
            </p>
            <p className="org-product-telemetry-panel__callout-body">
              {activeMeta.explanation}
            </p>
            <p className="org-product-telemetry-panel__callout-tab">
              {showingUsers ? activeMeta.userHint : activeMeta.roleHint}
            </p>
          </div>

          <Table
            columns={tableColumns}
            data={tableData}
            loading={false}
            scrollable
            maxHeight="calc(90vh - 24rem)"
            pagination
            defaultRowsPerPage={10}
            rowKey={(r: any) => r.id}
            emptyContent={
              <div className="text-sm text-gray-500 py-6 text-center">
                {showingUsers
                  ? "No user details for this metric yet. Create and materialize the detail Endpoint in PostHog, then re-run the rollup."
                  : "No role breakdown"}
              </div>
            }
          />
        </div>
      ) : null}
    </div>
  );
};

export default OrgProductTelemetryPanel;
